// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck - renderNode callbacks from @contentful/rich-text-react-renderer are untyped
import { useQuery } from '@apollo/client/react'
import { Options } from '@contentful/rich-text-react-renderer'
import { BLOCKS } from '@contentful/rich-text-types'
import { RichTextLinks } from '@/atoms/ContentfulRichText'
import { Image } from '@/atoms/Image'
import { Footer, ParagraphXL, TitleLG } from '@/atoms/Text'
import { EmbeddedVideo } from '@/molecules/EmbeddedVideo'
import { getContentfulClient } from '@/services/ApolloClient'
import { GET_GIVEAWAY_ASSET, GET_GIVEAWAY_YOUTUBE_TRAILER } from '@/services/GiveawayService/GiveawayService'
import type { Asset } from '@/types/codegen-contentful'
import { useLocale } from '@/utils/LocaleUtil'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'

function GiveawayEmbeddedEntry({ entryId }: { entryId: string }) {
  const { locale } = useLocale()
  const { isPreview } = useSafeRouter()
  const client = getContentfulClient({ locale, preview: isPreview })
  const { data } = useQuery<{
    youtubeTrailerId?: { __typename?: 'YoutubeTrailerId'; youtubeId?: string | null } | null
  }>(GET_GIVEAWAY_YOUTUBE_TRAILER, {
    variables: { id: entryId, preview: isPreview ?? false },
    client,
    skip: !entryId,
  })
  const entry = data?.youtubeTrailerId
  if (!entry?.__typename || entry.__typename !== 'YoutubeTrailerId' || !entry.youtubeId) return null
  const url = `https://www.youtube-nocookie.com/embed/${entry.youtubeId}`
  return <EmbeddedVideo url={url} className="w-full" />
}

function GiveawayEmbeddedAsset({ assetId }: { assetId: string }) {
  const { locale } = useLocale()
  const { isPreview } = useSafeRouter()
  const client = getContentfulClient({ locale, preview: isPreview })
  const { data } = useQuery<{ asset?: Asset | null }>(GET_GIVEAWAY_ASSET, {
    variables: { id: assetId },
    client,
    skip: !assetId,
  })
  const asset = data?.asset
  if (!asset?.url) return null
  const isImage = asset.contentType?.startsWith('image/')
  if (!isImage) return null
  const origWidth = asset.width ?? undefined
  const origHeight = asset.height ?? undefined
  let width = origWidth
  let height = origHeight
  if (typeof origWidth === 'number' && typeof origHeight === 'number' && origWidth > 900) {
    width = 900
    height = Math.round(900 * (origHeight / origWidth))
  }
  return <Image src={asset.url} alt={asset.title ?? ''} width={width} height={height} />
}

export const giveawayRichTextOverrides = (options: Options, links?: RichTextLinks) => {
  void links // links not used; assets are fetched client-side by asset id
  return {
    ...options,
    renderNode: {
      ...options.renderNode,
      [BLOCKS.PARAGRAPH]: (_, children) => (
        <ParagraphXL color="giveaway-text-subtle-light" weight="normal" className="mb-10 last:mb-0">
          {children}
        </ParagraphXL>
      ),
      [BLOCKS.HEADING_2]: (node, children) => (
        <TitleLG color="giveaway-text-primary-dark" className="md:photon-heading-sm mb-3 font-semibold md:font-bold">
          {children}
        </TitleLG>
      ),
      [BLOCKS.UL_LIST]: (_, children) => <ul className="mb-10 flex list-disc flex-col gap-2 pl-6">{children}</ul>,
      [BLOCKS.EMBEDDED_ASSET]: (node) => {
        const targetId = node.data?.target?.sys?.id
        if (!targetId || typeof targetId !== 'string') return null
        return <GiveawayEmbeddedAsset assetId={targetId} />
      },
      [BLOCKS.EMBEDDED_ENTRY]: (node) => {
        const targetId = node.data?.target?.sys?.id
        if (!targetId || typeof targetId !== 'string') return null
        return <GiveawayEmbeddedEntry entryId={targetId} />
      },
    },
  }
}

export const giveawayRulesRichTextOverrides = (options: Options) => {
  return {
    ...options,
    renderNode: {
      ...options.renderNode,
      [BLOCKS.PARAGRAPH]: (_, children) => (
        <Footer color="white" weight="normal" className="mb-2 block last:mb-0">
          {children}
        </Footer>
      ),
    },
  }
}
